import { ReactComponent as Group } from "assets/images/v2/Group.svg";
import { getLoanId, getNhDealApplicationId, setProfileActiveTab } from "features/authSlice";
import useDesktopChecker from "hooks/useDesktopChecker";
import { useLoanSummaryData } from "hooks/useLoanSummaryData";
import { PropertyLocation, useGetLoanSummaryDetailQuery } from "pages/dashboard/home/dashboardService";
import { useId, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import {
  LOAN_DETAILS_CARD_ADDRESS_LABEL,
  LOAN_DETAILS_CARD_ERROR_MESSAGE,
  LOAN_DETAILS_CARD_LOAN_AMOUNT_LABEL,
  LOAN_DETAILS_CARD_LOAN_NUMBER_LABEL,
  LOAN_DETAILS_CARD_LOAN_PURPOSE_LABEL,
  LOAN_DETAILS_CARD_PENDING_STATUS_TEXT as PENDING_STATUS_TEXT,
  VIEW_DETAILS_BUTTON_TEXT,
} from "utilities/Constant";
import styles from "./loan-details-card.module.scss";
import { NavigationIds } from "components/v2/shared-navigation/SharedNavigation";
import { formatLoanAmount } from "utilities/string";

export default function LoanDetailsCard() {
  const uid = useId();
  const isDesktop = useDesktopChecker();
  const nhDealApplicationId = useSelector(getNhDealApplicationId);
  const loanId = useSelector(getLoanId);

  const [titleClassName] = useState("label-medium-regular");
  const [valueClassName] = useState("label-large-medium");
  const { data: loanSummary, isError } = useGetLoanSummaryDetailQuery({ nhDealApplicationId, loanId, persistState: false });
  const { hasLoanSummaryData } = useLoanSummaryData();
  const { data: featureFlag } = useGetFeatureFlagsQuery();
  const dispatch = useDispatch();

  const isAddressEmpty = (value?: string) => !value || ["", "tbd", "tba"].includes(value.toLowerCase());
  const isFieldEmpty = (value?: string) => !value || value.trim() === "";

  const formatPropertyLocation = (propertyLocation?: PropertyLocation): string => {
    if (!propertyLocation) return PENDING_STATUS_TEXT;

    const { addressLine1, addressLine2, cityName, stateCode, postalCode } = propertyLocation;

    if (isAddressEmpty(addressLine1) && isAddressEmpty(addressLine2) && isFieldEmpty(cityName) && isFieldEmpty(stateCode) && isFieldEmpty(postalCode)) {
      return PENDING_STATUS_TEXT;
    }

    const parts: string[] = [];

    if (!isAddressEmpty(addressLine1)) parts.push(addressLine1!);
    if (!isAddressEmpty(addressLine2)) parts.push(addressLine2!);
    if (!isFieldEmpty(cityName)) parts.push(cityName!);

    if (!isFieldEmpty(stateCode) && !isFieldEmpty(postalCode)) {
      parts.push(`${stateCode!} ${postalCode!}`);
    } else if (!isFieldEmpty(stateCode)) {
      parts.push(stateCode!);
    } else if (!isFieldEmpty(postalCode)) {
      parts.push(postalCode!);
    }

    return parts.join(", ");
  };

  const buildCityStateParts = (cityName?: string, stateCode?: string, postalCode?: string): string[] => {
    const parts: string[] = [];
    if (!isFieldEmpty(cityName)) parts.push(cityName!);
    if (!isFieldEmpty(stateCode) && !isFieldEmpty(postalCode)) {
      parts.push(`${stateCode!} ${postalCode!}`);
    } else if (!isFieldEmpty(stateCode)) {
      parts.push(stateCode!);
    } else if (!isFieldEmpty(postalCode)) {
      parts.push(postalCode!);
    }
    return parts;
  };

  const renderPropertyAddress = (propertyLocation?: PropertyLocation) => {
    if (!propertyLocation) {
      return (
        <span
          className={`label-large-medium ${styles.propertyAddressValue}`}
          data-testid="property-address-value"
          aria-labelledby={`property-address-label-${uid}`}
          aria-live="polite"
        >
          {PENDING_STATUS_TEXT}
        </span>
      );
    }

    const { addressLine1, addressLine2, cityName, stateCode, postalCode } = propertyLocation;

    // Check if all fields are empty
    if (isAddressEmpty(addressLine1) && isAddressEmpty(addressLine2) && isFieldEmpty(cityName) && isFieldEmpty(stateCode) && isFieldEmpty(postalCode)) {
      return (
        <span
          className={`label-large-medium ${styles.propertyAddressValue}`}
          data-testid="property-address-value"
          aria-labelledby={`property-address-label-${uid}`}
          aria-live="polite"
        >
          {PENDING_STATUS_TEXT}
        </span>
      );
    }

    // Check if any address lines are present
    const hasAddressLines = !isAddressEmpty(addressLine1) || !isAddressEmpty(addressLine2);

    // Mobile view with address lines - render in two lines
    if (!isDesktop && hasAddressLines) {
      const addressLinesParts: string[] = [];
      if (!isAddressEmpty(addressLine1)) addressLinesParts.push(addressLine1!);
      if (!isAddressEmpty(addressLine2)) addressLinesParts.push(addressLine2!);
      const cityStateParts = buildCityStateParts(cityName, stateCode, postalCode);

      return (
        <div className={styles.propertyAddressValue} data-testid="property-address-value" aria-labelledby={`property-address-label-${uid}`} aria-live="polite">
          <span className="label-large-medium">{addressLinesParts.join(" ")}</span>
          <span className="label-medium-regular">{cityStateParts.join(", ")}</span>
        </div>
      );
    }

    // All other cases - render as single line
    return (
      <span
        className={`label-large-medium ${styles.propertyAddressValue}`}
        data-testid="property-address-value"
        aria-labelledby={`property-address-label-${uid}`}
        aria-live="polite"
      >
        {formatPropertyLocation(propertyLocation)}
      </span>
    );
  };

  const handleViewLoanDetailsClick = () => {
    dispatch(setProfileActiveTab(NavigationIds.LoanDetails));
  };

  const renderValue = (value: string | number | undefined, idPrefix: string) => {
    const labelId = `${idPrefix}-label-${uid}`;

    if (value === null || value === undefined || value === "") {
      return (
        <span
          className={valueClassName}
          aria-labelledby={labelId}
          aria-live="polite"
          data-testid={`${idPrefix}-value`}
        >
          {PENDING_STATUS_TEXT}
        </span>
      );
    }

    return (
      <span
        className={valueClassName}
        aria-labelledby={labelId}
        aria-live="polite"
        data-testid={`${idPrefix}-value`}
      >
        {value}
      </span>
    );
  };

  return (
    <div
      className={styles.content}
      data-testid="loan-details-card"
      aria-label="Loan details card"
    >
      <div className={styles.heading} data-testid="loan-details-heading">
        <div className={styles.myLoan}>
          {isDesktop && <h5 className={`heading-medium ${styles.myLoanHeading}`} data-testid="loan-title">My Loan</h5>}
          {!isDesktop && <h6 className={`heading-medium ${styles.myLoanHeading}`} data-testid="loan-title">My Loan</h6>}
        </div>
      </div>

      <div className={styles.loanDetailsCard} data-testid="loan-details-wrapper">
        <div className={isError ? styles.loanDetailsErrorWrapper : styles.loanDetailsWrapper}>
          <div
            className={isError ? styles.loanContentError : styles.loanContent}
            data-testid={isError ? "loan-error-content" : "loan-content"}
          >
            {isError ? (
              <div
                className={styles.errorWrapper}
                role="alert"
                aria-live="assertive"
                data-testid="loan-details-error"
              >
                <Group className={styles.errorIcon} aria-hidden="true" data-testid="loan-error-icon" />
                <div className={titleClassName}>{LOAN_DETAILS_CARD_ERROR_MESSAGE}</div>
              </div>
            ) : (
              <>
                <span
                  className={`label-medium-regular ${styles.propertyAddressTitle}`}
                  id={`property-address-label-${uid}`}
                  data-testid="property-address-label">
                  {LOAN_DETAILS_CARD_ADDRESS_LABEL}
                </span>
                {renderPropertyAddress(loanSummary?.propertyLocation)}
              </>
            )}
          </div>
        </div>

        {isError ? (
          <div className={styles.errorPlaceholder} data-testid="error-placeholder"></div>
        ) : (
          <div className={styles.keyValueSectionWrapper} data-testid="loan-key-value-section">

            <div className={`${styles.keyValuePair} ${styles.loanPurpose}`} data-testid="loan-purpose-section">
              <span
                className={titleClassName}
                id={`loan-purpose-label-${uid}`}
                data-testid="loan-purpose-label">
                {LOAN_DETAILS_CARD_LOAN_PURPOSE_LABEL}
              </span>
              {renderValue(loanSummary?.loanPurpose, "loan-purpose")}
            </div>

            <div className={styles.keyValuePair} data-testid="loan-amount-section">
              <span
                className={titleClassName}
                id={`loan-amount-label-${uid}`}
                data-testid="loan-amount-label">
                {LOAN_DETAILS_CARD_LOAN_AMOUNT_LABEL}
              </span>
              {renderValue(formatLoanAmount(loanSummary?.loanAmount), "loan-amount")}
            </div>

            <div className={styles.keyValuePair} data-testid="loan-number-section">
              <span
                className={titleClassName}
                id={`loan-number-label-${uid}`}
                data-testid="loan-number-label">
                {LOAN_DETAILS_CARD_LOAN_NUMBER_LABEL}
              </span>
              {renderValue(loanSummary?.loanNumber, "loan-number")}
            </div>
          </div>
        )}

        {featureFlag?.loanDetailsEnabled && hasLoanSummaryData && (
          <div
            className={isError ? styles.viewDetailsContainerError : styles.viewDetailsContainer}
            data-testid="view-details-container"
          >
            <button className={styles.viewDetailsWrapper} onClick={handleViewLoanDetailsClick} aria-label="View all tasks">
              <div
                className={`label-large-medium ${styles.viewDetails}`}
                aria-label="View loan details"
                data-testid="view-details">{VIEW_DETAILS_BUTTON_TEXT}</div>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
